import {
  CreateClusterAndRelationshipPayload,
  CreateDashboardClassClusterReportPayload,
  CreateNewClassificationTagPayload,
  CreateNewReportsForClusterPayload,
  CreateScheduledWidgetPayload,
  DeleteClusterFrequencyReportPayload,
  DeleteScheduledWidgetPayload,
  GetClassificationTagsDataPayload,
  GetClustersPayload,
  GetNewClusterDataPayload,
  GetReportsForClusterDataPayload,
  GetSavedReportsForClusterDataSuccessPayload,
  OnDemandReportPayload,
  ScheduledWidgetsBasePayload,
  UpdateClustersTableDataPayload,
  UpdateRegularClusterReportPayload,
  UpdateScheduledWidgetPayload,
  UpdateSponsorDashReportPayload,
} from '@/store/reportCenter/actions';
import toastMessage from '@components/Toast';
import { AxiosInstance, AxiosResponse } from 'axios';

// Updated method signatures
export default class ReportCenterService {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getClusters(payload: GetClustersPayload) {
    return this.client.post('/v2/report-center/get-clusters/', payload);
  }

  async getClassificationTagsData(payload: GetClassificationTagsDataPayload) {
    return this.client.post(
      '/v2/report-center/get-classification-tags-data/',
      payload,
    );
  }

  async createNewClassificationTag(payload: CreateNewClassificationTagPayload) {
    return this.client.post(
      '/v2/report-center/create-new-classification-tag/',
      payload,
    );
  }

  async getNewClusterData(payload: GetNewClusterDataPayload) {
    return this.client.post('/v2/report-center/get-new-cluster-data/', payload);
  }

  async createClusterAndRelationship(
    payload: CreateClusterAndRelationshipPayload,
  ) {
    return this.client.post(
      '/v2/report-center/create-new-cluster-and-relationship/',
      payload,
    );
  }

  async getReportsForClusterData(
    payload: GetReportsForClusterDataPayload,
    options: { headers: { Authorization: string; 'Content-Type': string } },
  ) {
    return this.client.post(
      '/v2/report-center/get-reports-for-cluster-data/',
      payload,
      options,
    );
  }

  async getSavedReportsForClusterData(
    payload: GetReportsForClusterDataPayload,
    options: { headers: { Authorization: string; 'Content-Type': string } },
  ): Promise<
    AxiosResponse<{ data: GetSavedReportsForClusterDataSuccessPayload }>
  > {
    return this.client.post(
      '/v2/report-center/get-reports-for-cluster-data/',
      payload,
      options,
    );
  }

  async createNewReportsForCluster(payload: CreateNewReportsForClusterPayload) {
    return this.client.post(
      '/v2/report-center/create-new-reports-for-cluster/',
      payload,
    );
  }

  async createDashboardClassClusterReport(
    payload: CreateDashboardClassClusterReportPayload,
  ) {
    return this.client.post(
      '/v2/report-center/create-dashboard-class-cluster-report/',
      payload,
    );
  }

  async updateRegularClusterReport(payload: UpdateRegularClusterReportPayload) {
    return this.client.post(
      '/v2/report-center/update-regular-cluster-report/',
      payload,
    );
  }

  async updateSponsorDashReport(payload: UpdateSponsorDashReportPayload) {
    return this.client.post(
      '/v2/report-center/update-sponsor-dash-report/',
      payload,
    );
  }

  async deleteClusterFrequencyReport(
    payload: DeleteClusterFrequencyReportPayload,
  ) {
    return this.client.post(
      '/v2/report-center/delete-cluster-frequency-report/',
      payload,
    );
  }

  async getClassTagsTableData() {
    return this.client.get('/v2/report-center/get-class-tags-table-data/');
  }

  async updateClassTagData(payload: CreateNewClassificationTagPayload) {
    return this.client.post(
      '/v2/report-center/update-class-tag-data/',
      payload,
    );
  }

  async getClustersTableData() {
    return this.client.get('/v2/report-center/get-clusters-table-data/');
  }

  async updateClustersTableData(payload: UpdateClustersTableDataPayload) {
    return this.client.post(
      '/v2/report-center/update-clusters-table-data/',
      payload,
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Scheduled widgets (report_widget_* tables)                         */
  /* ------------------------------------------------------------------ */

  async getScheduledWidgetsReferenceData(payload: ScheduledWidgetsBasePayload) {
    return this.client.post(
      '/v2/report-center/scheduled-widgets/reference-data/',
      payload,
    );
  }

  async getScheduledWidgets(payload: ScheduledWidgetsBasePayload) {
    return this.client.post(
      '/v2/report-center/scheduled-widgets/get/',
      payload,
    );
  }

  async createScheduledWidget(payload: CreateScheduledWidgetPayload) {
    return this.client.post(
      '/v2/report-center/scheduled-widgets/create/',
      payload,
    );
  }

  async updateScheduledWidget(payload: UpdateScheduledWidgetPayload) {
    return this.client.post(
      '/v2/report-center/scheduled-widgets/update/',
      payload,
    );
  }

  async deleteScheduledWidget(payload: DeleteScheduledWidgetPayload) {
    return this.client.post(
      '/v2/report-center/scheduled-widgets/delete/',
      payload,
    );
  }

  async generateOnDemandReport(payload: OnDemandReportPayload) {
    try {
      const response = await this.client.post(
        '/v2/report-center/on-demand-report-generation/',
        payload,
      );

      // Basic response validation
      if (!response.data) {
        toastMessage.error('Invalid API response format');
        return response.data;
      }

      // Example: if backend returns a `success` boolean
      if (response.data.success === true) {
        toastMessage.success(
          payload.is_monte_carlo
            ? 'Your Cash Consumption Simulation trigger request has been received.'
            : 'Your on-demand report request has been received. You will receive it shortly via email.',
        );
      } else if (response.data.success === false && response.data.message) {
        toastMessage.error(`Report request failed: ${response.data.message}`);
      } else {
        toastMessage.info(
          payload.is_monte_carlo
            ? 'Your Cash Consumption Simulation request was sent. Processing may take a few minutes.'
            : 'Your request was sent. Processing may take a few minutes.',
        );
      }

      return response.data;
    } catch (error: any) {
      console.error('On-demand report generation error:', error);
      toastMessage.error(
        'Failed to generate on-demand report: ' +
          (error.message || 'Unknown error'),
      );
      throw error;
    }
  }
}
